import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  DialogContentText,
  Pagination,
  CircularProgress,
  TextField,
  Grid,
  TableSortLabel,
  Collapse,
  Card,
  CardContent,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import ptBR from 'date-fns/locale/pt-BR';
import api from '../../services/api';
import Layout from '../Layout';

const SubstituicoesResponsaveisPanel = () => {
  const [substituicoes, setSubstituicoes] = useState([]);
  const [responsaveis, setResponsaveis] = useState([]);
  const [empresasAtivas, setEmpresasAtivas] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [editingSubstituicao, setEditingSubstituicao] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [paginacao, setPaginacao] = useState({
    pagina_atual: 1,
    total_paginas: 1,
    total_registros: 0,
    registros_por_pagina: 50
  });
  const [filtros, setFiltros] = useState({
    empresa: '',
    responsavel_principal: '',
    responsavel_substituto: '',
    data_inicio_de: null,
    data_inicio_ate: null,
    data_fim_de: null,
    data_fim_ate: null
  });
  const [ordenacao, setOrdenacao] = useState({
    order_by: 'ID',
    order_direction: 'asc'
  });
  const [formData, setFormData] = useState({
    IDEmpresa: '',
    IDResponsavelSubstituto: '',
    DataInicio: new Date(),
    DataFim: null,
    empresaSelecionada: null,
    responsavelSubstitutoSelecionado: null,
  });

  const fetchResponsaveis = useCallback(async () => {
    try {
      const response = await api.get('/responsaveis');
      setResponsaveis(response.data);
    } catch (error) {
      console.error('Erro ao buscar responsáveis:', error);
      setError('Erro ao carregar responsáveis. Por favor, tente novamente.');
    }
  }, []);

  const fetchEmpresasAtivas = useCallback(async () => {
    try {
      // Buscar apenas empresas com responsável ativo
      
      const response = await api.get('/empresas-ativas');
      setEmpresasAtivas(response.data);
    } catch (error) {
      console.error('Erro ao buscar empresas ativas:', error);
      setError('Erro ao carregar empresas ativas. Por favor, tente novamente.');
    }
  }, []);

  const fetchSubstituicoes = useCallback(async (page = 1, customOrdenacao = null) => {
    try {
      setLoading(true);
      
      // Usar ordenação customizada ou a atual
      const ordenacaoAtual = customOrdenacao || ordenacao;
      
      // Construir query string com filtros e ordenação
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50',
        order_by: ordenacaoAtual.order_by,
        order_direction: ordenacaoAtual.order_direction
      });
      
      // Adicionar filtros se preenchidos
      if (filtros.empresa) params.append('empresa', filtros.empresa);
      if (filtros.responsavel_principal) params.append('responsavel_principal', filtros.responsavel_principal);
      if (filtros.responsavel_substituto) params.append('responsavel_substituto', filtros.responsavel_substituto);
      if (filtros.data_inicio_de && filtros.data_inicio_de instanceof Date) {
        params.append('data_inicio_de', filtros.data_inicio_de.toISOString().split('T')[0]);
      }
      if (filtros.data_inicio_ate && filtros.data_inicio_ate instanceof Date) {
        params.append('data_inicio_ate', filtros.data_inicio_ate.toISOString().split('T')[0]);
      }
      if (filtros.data_fim_de && filtros.data_fim_de instanceof Date) {
        params.append('data_fim_de', filtros.data_fim_de.toISOString().split('T')[0]);
      }
      if (filtros.data_fim_ate && filtros.data_fim_ate instanceof Date) {
        params.append('data_fim_ate', filtros.data_fim_ate.toISOString().split('T')[0]);
      }
      
      const response = await api.get(`/substituicoes-responsaveis?${params}`);
      
      // Verificar se a resposta tem a estrutura esperada
      if (response.data.dados && response.data.paginacao) {
        setSubstituicoes(response.data.dados);
        setPaginacao(response.data.paginacao);
      } else {
        // Fallback para formato antigo (sem paginação)
        setSubstituicoes(response.data);
        setPaginacao({
          pagina_atual: 1,
          total_paginas: 1,
          total_registros: response.data.length,
          registros_por_pagina: response.data.length
        });
      }
    } catch (error) {
      console.error('Erro ao buscar substituições:', error);
      setError('Erro ao carregar substituições. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [filtros, ordenacao]);

  useEffect(() => {
    fetchSubstituicoes(1);
    fetchResponsaveis();
    fetchEmpresasAtivas();
  }, [fetchSubstituicoes, fetchResponsaveis, fetchEmpresasAtivas]);



  const handlePageChange = (event, value) => {
    fetchSubstituicoes(value);
  };

  const handleSort = (campo) => {
    const novaOrdenacao = {
      order_by: campo,
      order_direction: ordenacao.order_by === campo && ordenacao.order_direction === 'asc' ? 'desc' : 'asc'
    };
    setOrdenacao(novaOrdenacao);
    // Fazer nova consulta com a ordenação aplicada e voltar para página 1
    fetchSubstituicoes(1, novaOrdenacao);
  };

  const handleFilterChange = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  const aplicarFiltros = () => {
    fetchSubstituicoes(1);
  };

  const limparFiltros = () => {
    setFiltros({
      empresa: '',
      responsavel_principal: '',
      responsavel_substituto: '',
      data_inicio_de: null,
      data_inicio_ate: null,
      data_fim_de: null,
      data_fim_ate: null
    });
    // Após limpar, buscar novamente
    setTimeout(() => {
      fetchSubstituicoes(1);
    }, 100);
  };

  const handleSubmit = async () => {
    try {
      setDialogError('');
      
      // Validações básicas
      if (!formData.IDEmpresa || !formData.IDResponsavelSubstituto || !formData.DataInicio || !formData.DataFim) {
        setDialogError('Todos os campos são obrigatórios.');
        return;
      }

      // Validar se data fim é posterior à data início
      if (formData.DataFim <= formData.DataInicio) {
        setDialogError('A data de fim deve ser posterior à data de início.');
        return;
      }

      // Verificar sobreposição de datas na mesma empresa
      const sobreposicao = substituicoes.find(s => 
        s.IDEmpresa === formData.IDEmpresa &&
        s.ID !== editingSubstituicao?.ID &&
        (
          // A nova substituição inicia durante um período existente
          (formData.DataInicio >= new Date(s.DataInicio) && formData.DataInicio <= new Date(s.DataFim)) ||
          // A nova substituição termina durante um período existente
          (formData.DataFim >= new Date(s.DataInicio) && formData.DataFim <= new Date(s.DataFim)) ||
          // A nova substituição engloba completamente um período existente
          (formData.DataInicio <= new Date(s.DataInicio) && formData.DataFim >= new Date(s.DataFim)) ||
          // Um período existente engloba completamente a nova substituição
          (new Date(s.DataInicio) <= formData.DataInicio && new Date(s.DataFim) >= formData.DataFim)
        )
      );

      if (sobreposicao) {
        setDialogError('Já existe uma substituição para esta empresa no período especificado. Os períodos não podem se sobrepor.');
        return;
      }

      // Preparar dados para envio
      const dadosParaEnvio = {
        IDEmpresa: formData.IDEmpresa,
        IDResponsavelSubstituto: formData.IDResponsavelSubstituto,
        DataInicio: formData.DataInicio ? formData.DataInicio.toISOString().split('T')[0] : null,
        DataFim: formData.DataFim ? formData.DataFim.toISOString().split('T')[0] : null,
      };

      console.log('Dados sendo enviados:', dadosParaEnvio);

      if (editingSubstituicao) {
        await api.put(`/substituicoes-responsaveis/${editingSubstituicao.ID}`, dadosParaEnvio);
        setSuccess('Substituição atualizada com sucesso!');
      } else {
        await api.post('/substituicoes-responsaveis', dadosParaEnvio);
        setSuccess('Substituição adicionada com sucesso!');
      }
      
      setOpenDialog(false);
      setEditingSubstituicao(null);
      setDialogError('');
      setFormData({
        IDEmpresa: '',
        IDResponsavelSubstituto: '',
        DataInicio: new Date(),
        DataFim: null,
        empresaSelecionada: null,
        responsavelSubstitutoSelecionado: null,
      });
      fetchSubstituicoes(paginacao.pagina_atual);
      
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (error) {
      console.error('Erro ao salvar substituição:', error);
      
      // Verificar se é um erro do backend com mensagem específica
      if (error.response && error.response.data && error.response.data.message) {
        setDialogError(error.response.data.message);
      } else if (error.response && error.response.status === 400) {
        setDialogError('Erro de validação. Verifique os dados e tente novamente.');
      } else {
        setDialogError('Erro ao salvar. Por favor, verifique os dados e tente novamente.');
      }
    }
  };

  const handleEdit = (substituicao) => {
    // Encontrar os objetos completos para o Autocomplete
    const empresaObj = Array.isArray(empresasAtivas) ? empresasAtivas.find(e => e.ID === substituicao.IDEmpresa) : null;
    const responsavelObj = Array.isArray(responsaveis) ? responsaveis.find(r => r.id === substituicao.IDResponsavelSubstituto) : null;
    
    setEditingSubstituicao(substituicao);
    setFormData({
      IDEmpresa: substituicao.IDEmpresa,
      IDResponsavelSubstituto: substituicao.IDResponsavelSubstituto,
      DataInicio: new Date(substituicao.DataInicio),
      DataFim: substituicao.DataFim ? new Date(substituicao.DataFim) : null,
      empresaSelecionada: empresaObj || null,
      responsavelSubstitutoSelecionado: responsavelObj || null,
    });
    setOpenDialog(true);
  };

  const handleDelete = async (substituicao) => {
    setEditingSubstituicao(substituicao);
    setOpenConfirmDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/substituicoes-responsaveis/${editingSubstituicao.ID}`);
      setSuccess('Registro excluído com sucesso!');
      fetchSubstituicoes(paginacao.pagina_atual);
      setOpenConfirmDialog(false);
      
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (error) {
      console.error('Erro ao excluir registro:', error);
      setError('Erro ao excluir. Por favor, tente novamente.');
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  return (
    <Layout>
      <Container maxWidth="lg">
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h5" gutterBottom>
              Responsável Temporário
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gerenciar substituições temporárias de responsáveis por empresa
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant={showFilters ? "contained" : "outlined"}
              onClick={() => setShowFilters(!showFilters)}
              startIcon={<FilterIcon />}
            >
              Filtros
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={() => {
                setEditingSubstituicao(null);
                setFormData({
                  IDEmpresa: '',
                  IDResponsavelSubstituto: '',
                  DataInicio: new Date(),
                  DataFim: null,
                  empresaSelecionada: null,
                  responsavelSubstitutoSelecionado: null,
                });
                setOpenDialog(true);
              }}
              startIcon={<AddIcon />}
            >
              Nova Substituição
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Painel de Filtros */}
        <Collapse in={showFilters}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Filtros de Pesquisa
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Empresa"
                    value={filtros.empresa}
                    onChange={(e) => handleFilterChange('empresa', e.target.value)}
                    placeholder="Nome da empresa"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Responsável Principal"
                    value={filtros.responsavel_principal}
                    onChange={(e) => handleFilterChange('responsavel_principal', e.target.value)}
                    placeholder="Nome do responsável principal"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Responsável Substituto"
                    value={filtros.responsavel_substituto}
                    onChange={(e) => handleFilterChange('responsavel_substituto', e.target.value)}
                    placeholder="Nome do responsável substituto"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                    <DatePicker
                      label="Data Início - De"
                      value={filtros.data_inicio_de}
                      onChange={(newValue) => handleFilterChange('data_inicio_de', newValue)}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                    <DatePicker
                      label="Data Início - Até"
                      value={filtros.data_inicio_ate}
                      onChange={(newValue) => handleFilterChange('data_inicio_ate', newValue)}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                    <DatePicker
                      label="Data Fim - De"
                      value={filtros.data_fim_de}
                      onChange={(newValue) => handleFilterChange('data_fim_de', newValue)}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                    <DatePicker
                      label="Data Fim - Até"
                      value={filtros.data_fim_ate}
                      onChange={(newValue) => handleFilterChange('data_fim_ate', newValue)}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={aplicarFiltros}
                    sx={{ height: '56px' }}
                  >
                    Aplicar Filtros
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={limparFiltros}
                    startIcon={<ClearIcon />}
                    sx={{ height: '56px' }}
                  >
                    Limpar
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Collapse>

        {/* Informações de paginação */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Mostrando {substituicoes.length} de {paginacao.total_registros} registros
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Página {paginacao.pagina_atual} de {paginacao.total_paginas}
          </Typography>
        </Box>

        <TableContainer component={Paper} sx={{ mt: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={ordenacao.order_by === 'ID'}
                    direction={ordenacao.order_by === 'ID' ? ordenacao.order_direction : 'asc'}
                    onClick={() => handleSort('ID')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={ordenacao.order_by === 'NomeFantasia'}
                    direction={ordenacao.order_by === 'NomeFantasia' ? ordenacao.order_direction : 'asc'}
                    onClick={() => handleSort('NomeFantasia')}
                  >
                    Empresa
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={ordenacao.order_by === 'ResponsavelPrincipal'}
                    direction={ordenacao.order_by === 'ResponsavelPrincipal' ? ordenacao.order_direction : 'asc'}
                    onClick={() => handleSort('ResponsavelPrincipal')}
                  >
                    Responsável Principal
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={ordenacao.order_by === 'ResponsavelSubstituto'}
                    direction={ordenacao.order_by === 'ResponsavelSubstituto' ? ordenacao.order_direction : 'asc'}
                    onClick={() => handleSort('ResponsavelSubstituto')}
                  >
                    Responsável Substituto
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={ordenacao.order_by === 'DataInicio'}
                    direction={ordenacao.order_by === 'DataInicio' ? ordenacao.order_direction : 'asc'}
                    onClick={() => handleSort('DataInicio')}
                  >
                    Data Início
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={ordenacao.order_by === 'DataFim'}
                    direction={ordenacao.order_by === 'DataFim' ? ordenacao.order_direction : 'asc'}
                    onClick={() => handleSort('DataFim')}
                  >
                    Data Fim
                  </TableSortLabel>
                </TableCell>
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={40} />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Carregando dados...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : substituicoes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Nenhum registro encontrado
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                substituicoes.map((registro) => (
                  <TableRow key={registro.ID}>
                    <TableCell>{registro.ID}</TableCell>
                    <TableCell>{registro.NomeFantasia}</TableCell>
                    <TableCell>{registro.ResponsavelPrincipal}</TableCell>
                    <TableCell>{registro.ResponsavelSubstituto}</TableCell>
                    <TableCell>{formatDate(registro.DataInicio)}</TableCell>
                    <TableCell>{formatDate(registro.DataFim)}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleEdit(registro)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(registro)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Componente de Paginação */}
        {paginacao.total_paginas > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={paginacao.total_paginas}
              page={paginacao.pagina_atual}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
              disabled={loading}
            />
          </Box>
        )}

        {/* Dialog para adicionar/editar registro */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingSubstituicao ? 'Editar Substituição' : 'Nova Substituição'}
          </DialogTitle>
          <DialogContent>
            {dialogError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError('')}>
                {dialogError}
              </Alert>
            )}
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Autocomplete
                fullWidth
                options={empresasAtivas}
                getOptionLabel={(option) => option.NomeFantasia || ''}
                value={formData.empresaSelecionada}
                onChange={(event, newValue) => {
                  setFormData({ 
                    ...formData, 
                    IDEmpresa: newValue ? newValue.ID : '',
                    empresaSelecionada: newValue 
                  });
                }}
                isOptionEqualToValue={(option, value) => option.ID === value.ID}
                filterOptions={(options, { inputValue }) => {
                  return options.filter(option =>
                    option.NomeFantasia.toLowerCase().includes(inputValue.toLowerCase())
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Empresa"
                    placeholder="Digite para buscar empresa..."
                    required
                  />
                )}
                noOptionsText="Nenhuma empresa encontrada"
              />

              <Autocomplete
                fullWidth
                options={responsaveis}
                getOptionLabel={(option) => option.Responsavel || ''}
                value={formData.responsavelSubstitutoSelecionado}
                onChange={(event, newValue) => {
                  setFormData({ 
                    ...formData, 
                    IDResponsavelSubstituto: newValue ? newValue.id : '',
                    responsavelSubstitutoSelecionado: newValue 
                  });
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(options, { inputValue }) => {
                  return options.filter(option =>
                    option.Responsavel.toLowerCase().includes(inputValue.toLowerCase())
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Responsável Substituto"
                    placeholder="Digite para buscar responsável..."
                    required
                  />
                )}
                noOptionsText="Nenhum responsável encontrado"
              />

              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                <DatePicker
                  label="Data de Início"
                  value={formData.DataInicio}
                  onChange={(newValue) => {
                    setFormData({ ...formData, DataInicio: newValue });
                  }}
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
              </LocalizationProvider>

              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                <DatePicker
                  label="Data de Fim"
                  value={formData.DataFim}
                  onChange={(newValue) => {
                    setFormData({ ...formData, DataFim: newValue });
                  }}
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
              </LocalizationProvider>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setOpenDialog(false);
              setDialogError('');
            }}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} variant="contained" color="primary">
              Salvar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de confirmação para exclusão */}
        <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}>
          <DialogTitle>Confirmar Exclusão</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Tem certeza que deseja excluir esta substituição? Esta ação não pode ser desfeita.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenConfirmDialog(false)}>Cancelar</Button>
            <Button onClick={confirmDelete} color="error" variant="contained">
              Excluir
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default SubstituicoesResponsaveisPanel; 